import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { formatShortDate, timeOnly } from "../chiefComplaints/dateHelpers";
import { treatmentBand } from "../treatmentStatus";
import { recentEpisodeSinceWhen, recentResolveSinceWhen } from "./episodeDates";
import DiagnosisService from "./DiagnosisService";
import TreatmentStatusChart from "./TreatmentStatusChart";
import { fetchComplaints, fetchLabs, fetchMedicines } from "./linkedRefsData";
import BulletListCell from "./BulletListCell";
import { DX_V2_REFRESH } from "./events";
import { CalendarIcon } from "../labInvestigations/labIcons";
import ModalPortal from "../common/ModalPortal";

/**
 * "View trend" modal for a diagnosis. Audit rows carry a trailing flag column;
 * clicking it opens a review dialog that lets a clinician either correct the
 * row's display fields or flag the whole row as incorrect. Rules (locked
 * during design):
 *
 *   1. The live entry IS flaggable for correction.
 *   2. Once corrected, the amber flag opens history only — no chained corrections.
 *   3. A corrected row cannot later be marked incorrect.
 *   4. A row marked incorrect cannot be corrected.
 *   5. The original panel in the history popup gets a red "Marked incorrect" pill
 *      if the row was later marked incorrect (currently unreachable given 3+4,
 *      but kept for future-proofing).
 *
 * Rows flagged incorrect are struck-through in the audit table AND excluded
 * from every derived stat + the trend chart — the promise the success dialog
 * makes ("excluded from trends and graphs") is honoured both client-side
 * (chart) and server-side (Episodes / First / Latest / CurrentSinceWhen).
 */

const MAX_DESCRIPTION_WORDS = 250;

// Actor label — mirrors the reporter fallback below so BY-lines in the
// incorrect / correction popups read the same way as the audit rows.
const actorLabel = (actor, enrichedName) => {
  if (enrichedName) return enrichedName;
  if (!actor) return "—";
  const who = actor.who ? actor.who.charAt(0).toUpperCase() + actor.who.slice(1) : "";
  return actor.whoId ? `${who || "User"} #${actor.whoId}` : who || "—";
};

// Format an ISO instant as "HH:MM am/pm" — matches the CC v2 marked-incorrect
// popup so the two sections feel consistent.
const formatTimeOnly = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ampm}`;
};

// One of three states — drives colour + click behaviour on the flag column.
const rowState = (row) => {
  if (row.markedIncorrect) return "incorrect";
  if (row.correction) return "corrected";
  return "live";
};

// Small red flag SVG; grey when unused, red when the row is incorrect or
// corrected. Matches the visual language of the CC v2 trend modal.
const FlagIcon = ({ active }) => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M3.5 2.25v11.5"
      stroke={active ? "#b91c1c" : "#6a7282"}
      strokeWidth="1.4"
      strokeLinecap="round"
    />
    <path
      d="M3.5 2.5h7.75l-1.25 2.5 1.25 2.5H3.5"
      fill={active ? "#b91c1c" : "transparent"}
      stroke={active ? "#b91c1c" : "#6a7282"}
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  </svg>
);
FlagIcon.propTypes = { active: PropTypes.bool };

// Truncate a word-limited textarea payload — same helper used in DiagnosisForm.
const clampWords = (value, max) => {
  const words = value.split(/\s+/).filter(Boolean);
  if (words.length <= max) return value;
  return words.slice(0, max).join(" ");
};
// Live counter for the "N / 250 words" hint. Mirrors DiagnosisForm's helper
// so the two Description surfaces read consistently.
const countWords = (s) => {
  const trimmed = (s || "").trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
};

// ─── Review dialog (2 CTAs) ────────────────────────────────────────────────
const ReviewEntryDialog = ({ submitting, onMarkIncorrect, onCorrect, onClose }) => (
  <div className="cc-modal-backdrop delc-backdrop" onMouseDown={submitting ? null : onClose}>
    <div
      className="alg-review cc-review-wide"
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <header className="alg-review-head">
        <h3 className="alg-review-title">Review this entry</h3>
        <button
          type="button"
          className="alg-review-close"
          onClick={onClose}
          disabled={submitting}
          aria-label="Close"
        >
          ×
        </button>
      </header>
      <p className="alg-review-body">
        You can correct this entry&rsquo;s details, or mark the whole entry
        as incorrect.
      </p>
      <p className="alg-review-body">
        <strong>Please note: Correction is allowed only once.</strong>
      </p>
      <footer className="alg-review-foot cc-review-foot">
        <button
          type="button"
          className="alg-review-mark"
          onClick={onMarkIncorrect}
          disabled={submitting}
        >
          Mark as incorrect
        </button>
        <button
          type="button"
          className="alg-success-done"
          onClick={onCorrect}
          disabled={submitting}
        >
          Yes, correct entry
        </button>
      </footer>
    </div>
  </div>
);
ReviewEntryDialog.propTypes = {
  submitting: PropTypes.bool,
  onMarkIncorrect: PropTypes.func.isRequired,
  onCorrect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

// ─── Success dialog (post-mutation confirmation) ───────────────────────────
const SuccessDialog = ({ mode, onDone }) => (
  <div className="cc-modal-backdrop delc-backdrop" onMouseDown={onDone}>
    <div
      className="alg-success"
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        className="alg-review-close"
        onClick={onDone}
        aria-label="Close"
      >
        ×
      </button>
      <span className="alg-success-medallion" aria-hidden="true">
        <FlagIcon active />
      </span>
      <h3 className="alg-success-title">
        {mode === "incorrect" ? "Entry marked as incorrect" : "Entry corrected"}
      </h3>
      <p className="alg-success-body">
        {mode === "incorrect"
          ? "This entry is excluded from trends and graphs. It remains in the audit history."
          : "The audit entry now shows the corrected values. Its previous values are preserved in the correction history."}
      </p>
      <button type="button" className="alg-success-done" onClick={onDone}>
        Done
      </button>
    </div>
  </div>
);
SuccessDialog.propTypes = {
  mode: PropTypes.oneOf(["incorrect", "corrected"]).isRequired,
  onDone: PropTypes.func.isRequired,
};

// ─── Marked-incorrect audit dialog ─────────────────────────────────────────
const MarkedIncorrectDialog = ({ row, onClose }) => (
  <div className="cc-modal-backdrop delc-backdrop" onMouseDown={onClose}>
    <div
      className="alg-marked"
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <header className="alg-marked-head">
        <span className="alg-marked-eyebrow">
          <FlagIcon active /> MARKED INCORRECT
        </span>
        <button
          type="button"
          className="alg-review-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </header>
      <dl className="alg-marked-dl">
        <dt>BY</dt>
        <dd>{actorLabel(row.markedIncorrectBy, row.markedIncorrectByName)}</dd>
        <dt>ON</dt>
        <dd>{row.markedIncorrectAt ? formatShortDate(row.markedIncorrectAt) : "—"}</dd>
        <dt>AT</dt>
        <dd>{row.markedIncorrectAt ? formatTimeOnly(row.markedIncorrectAt) : "—"}</dd>
      </dl>
    </div>
  </div>
);
MarkedIncorrectDialog.propTypes = {
  row: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

// ─── Correction history dialog (Original vs Latest) ────────────────────────
const CorrectionHistoryDialog = ({ row, diagnosisName, medicineLabels, onClose }) => {
  const prev = row.correction && row.correction.previousSnapshot;
  const correctedAt = row.correction && row.correction.at;
  return (
    <div className="cc-modal-backdrop delc-backdrop" onMouseDown={onClose}>
      <div
        className="cc-correction-history"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="cc-corr-head">
          <div>
            <h3 className="cc-corr-title">Correction history</h3>
            <p className="cc-corr-sub">
              {diagnosisName} · corrected{" "}
              {correctedAt ? formatShortDate(correctedAt) : "—"}
              {correctedAt ? `, ${formatTimeOnly(correctedAt)}` : ""} by{" "}
              {actorLabel(row.correction && row.correction.by, row.correction && row.correction.byName)}
            </p>
          </div>
          <button
            type="button"
            className="alg-review-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <section className="cc-corr-panel">
          <div className="cc-corr-panel-head">
            <span className="cc-corr-panel-label">ORIGINAL ENTRY</span>
            {/* Rendered unconditionally to mirror the CC Correction-history
                dialog — the ORIGINAL panel always carries this pill so the
                reviewer sees at a glance that the row above was superseded,
                whether it was corrected in place or explicitly marked
                incorrect. */}
            <span className="cc-corr-pill is-incorrect">Marked incorrect</span>
          </div>
          <dl className="cc-corr-dl">
            <dt>Since when</dt>
            <dd>{prev ? formatShortDate(prev.sinceWhen) : "—"}</dd>
            <dt>To when</dt>
            <dd>{prev && prev.toWhen ? formatShortDate(prev.toWhen) : "—"}</dd>
            <dt>Description</dt>
            <dd>{(prev && prev.description) || "—"}</dd>
            <dt>Consulted doctor</dt>
            <dd>{prev ? (prev.consultedDoctor ? "Yes" : "No") : "—"}</dd>
            <dt>Medicines</dt>
            <dd>{prev ? (medicineLabels(prev.medicineRefs).join(", ") || "—") : "—"}</dd>
            <dt>Treatment status</dt>
            <dd>{prev ? (prev.ongoing ? "Ongoing" : "Resolved") : "—"}</dd>
          </dl>
        </section>

        <section className="cc-corr-panel is-latest">
          <div className="cc-corr-panel-head">
            <span className="cc-corr-panel-label">LATEST ENTRY</span>
            <span className="cc-corr-pill is-current">Current</span>
          </div>
          <dl className="cc-corr-dl">
            <dt>Since when</dt>
            <dd>{formatShortDate(row.sinceWhen)}</dd>
            <dt>To when</dt>
            <dd>{row.toWhen ? formatShortDate(row.toWhen) : "—"}</dd>
            <dt>Description</dt>
            <dd>{row.description || "—"}</dd>
            <dt>Consulted doctor</dt>
            <dd>{row.consultedDoctor ? "Yes" : "No"}</dd>
            <dt>Medicines</dt>
            <dd>{medicineLabels(row.medicineRefs).join(", ") || "—"}</dd>
            <dt>Treatment status</dt>
            <dd>{row.ongoing ? "Ongoing" : "Resolved"}</dd>
          </dl>
        </section>

        <footer className="cc-corr-foot">
          <button
            type="button"
            className="alg-success-done cc-corr-done"
            onClick={onClose}
          >
            Done
          </button>
        </footer>
      </div>
    </div>
  );
};
CorrectionHistoryDialog.propTypes = {
  row: PropTypes.object.isRequired,
  diagnosisName: PropTypes.string,
  medicineLabels: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

// ─── Correct entry editor ──────────────────────────────────────────────────
// Editable fields: dates, description, treatment status. Medicine / complaint
// / lab refs are inherited from the row being corrected — those are structural
// links, not typos; if a clinician wants to change what a diagnosis is linked
// to, they use Update Status which appends a new segment.
const CorrectEntryDialog = ({
  row,
  diagnosisName,
  medicineLabels,
  submitting,
  onCancel,
  onSave,
}) => {
  const isoDate = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (isNaN(d.getTime())) return "";
    // yyyy-mm-dd for native date input.
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const [sinceWhen, setSinceWhen] = useState(isoDate(row.sinceWhen));
  const [toWhen, setToWhen] = useState(isoDate(row.toWhen));
  const [description, setDescription] = useState(row.description || "");
  const [ongoing, setOngoing] = useState(!!row.ongoing);
  const [consulted, setConsulted] = useState(!!row.consultedDoctor);

  const toWhenBeforeSince = toWhen && sinceWhen && toWhen < sinceWhen;
  // Future dates aren't accepted for either boundary — the picker's `max`
  // already greys them out, but a manually-typed value can still land in
  // the future, so we double-guard by disabling Save.
  const todayStr = new Date().toISOString().slice(0, 10);
  const anyFuture =
    (sinceWhen && sinceWhen > todayStr) || (toWhen && toWhen > todayStr);
  const canSave =
    !!sinceWhen && !toWhenBeforeSince && !anyFuture && !submitting;

  const submit = () => {
    if (!canSave) return;
    onSave({
      ongoing,
      sinceWhen: new Date(sinceWhen).toISOString(),
      toWhen: toWhen ? new Date(toWhen).toISOString() : null,
      description,
      consultedDoctor: consulted,
      // Structural refs inherited verbatim.
      complaintIds: row.complaintIds || [],
      medicineRefs: row.medicineRefs || [],
      labRefs: row.labRefs || [],
    });
  };

  return (
    <div className="cc-modal-backdrop delc-backdrop" onMouseDown={submitting ? null : onCancel}>
      <div
        className="cc-correct-entry"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="cc-corr-head">
          <h3 className="cc-corr-title">
            Correct entry
            {diagnosisName && <span className="cc-corr-sub-inline"> · {diagnosisName}</span>}
          </h3>
          <button
            type="button"
            className="alg-review-close"
            onClick={onCancel}
            disabled={submitting}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="cc-correct-body">
          <div className="cc-correct-row">
            <div className="sec-field">
              <label className="sec-label" htmlFor="dx-corr-since">
                Since when <span className="sec-label-required">*</span>
              </label>
              {/* Same date-input treatment as the medicine Add flow —
                  bordered pill with a trailing calendar icon so all the
                  correction dialogs share one visual language. */}
              <span className="med3-date">
                <input
                  id="dx-corr-since"
                  className="med3-date-input"
                  type="date"
                  value={sinceWhen}
                  onChange={(e) => setSinceWhen(e.target.value)}
                  max={todayStr}
                  disabled={submitting}
                />
                <span className="med3-date-icon" aria-hidden="true"><CalendarIcon /></span>
              </span>
            </div>
            <div className="sec-field">
              <label className="sec-label" htmlFor="dx-corr-to">
                To when <span className="sec-label-note">optional</span>
              </label>
              <span className={`med3-date${toWhenBeforeSince ? " is-error" : ""}`}>
                <input
                  id="dx-corr-to"
                  className="med3-date-input"
                  type="date"
                  value={toWhen}
                  onChange={(e) => setToWhen(e.target.value)}
                  max={todayStr}
                  disabled={submitting}
                />
                <span className="med3-date-icon" aria-hidden="true"><CalendarIcon /></span>
              </span>
              {toWhenBeforeSince && (
                <p className="fam-field-error">To when cannot precede Since when.</p>
              )}
            </div>
          </div>

          {/* Treatment status + Consulted doctor share a row above the
              Description block. Both are compact segmented controls, so
              pairing them saves vertical space and puts the categorical
              decisions together before the free-text note. */}
          <div className="cc-correct-row">
            <div className="sec-field">
              <label className="sec-label">
                Treatment status <span className="sec-label-required">*</span>
              </label>
              <div className="cc-corr-seg" role="radiogroup" aria-label="Treatment status">
                {[{ v: true, label: "Ongoing" }, { v: false, label: "Resolved" }].map((o) => (
                  <button
                    key={String(o.v)}
                    type="button"
                    role="radio"
                    aria-checked={ongoing === o.v}
                    className={`cc-corr-seg-cell${ongoing === o.v ? " is-on" : ""}`}
                    onClick={() => setOngoing(o.v)}
                    disabled={submitting}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="sec-field">
              <label className="sec-label">
                Consulted doctor? <span className="sec-label-required">*</span>
              </label>
              <div className="cc-corr-seg" role="radiogroup" aria-label="Consulted doctor">
                {[{ v: true, label: "Yes" }, { v: false, label: "No" }].map((o) => (
                  <button
                    key={String(o.v)}
                    type="button"
                    role="radio"
                    aria-checked={consulted === o.v}
                    className={`cc-corr-seg-cell${consulted === o.v ? " is-on" : ""}`}
                    onClick={() => setConsulted(o.v)}
                    disabled={submitting}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="sec-field">
            <label className="sec-label" htmlFor="dx-corr-desc">
              Description <span className="sec-label-note">optional</span>
              <span className="dx-word-hint">
                {description
                  ? `${countWords(description)} / ${MAX_DESCRIPTION_WORDS} words`
                  : `Max ${MAX_DESCRIPTION_WORDS} words`}
              </span>
            </label>
            <textarea
              id="dx-corr-desc"
              className="sec-textarea"
              rows={4}
              value={description}
              onChange={(e) =>
                setDescription(clampWords(e.target.value, MAX_DESCRIPTION_WORDS))
              }
              disabled={submitting}
            />
          </div>
        </div>

        <footer className="cc-corr-foot">
          <button
            type="button"
            className="sec-btn sec-btn-ghost"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="alg-success-done cc-corr-save"
            onClick={submit}
            disabled={!canSave}
          >
            {submitting ? "Saving…" : "Save correction"}
          </button>
        </footer>
      </div>
    </div>
  );
};
CorrectEntryDialog.propTypes = {
  row: PropTypes.object.isRequired,
  diagnosisName: PropTypes.string,
  medicineLabels: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

// ─── TrendModal (main) ─────────────────────────────────────────────────────
const TrendModal = ({ patientId, diagnosis, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [medicineIndex, setMedicineIndex] = useState({});
  const [complaintIndex, setComplaintIndex] = useState({});
  const [labIndex, setLabIndex] = useState({});

  // Correction / mark-incorrect flow state.
  const [flagTarget, setFlagTarget] = useState(null); // {row, index} | null
  const [dialogMode, setDialogMode] = useState(null); // "review" | "correct" | "history" | "incorrect" | null
  const [successMode, setSuccessMode] = useState(null); // "incorrect" | "corrected" | null
  const [submitting, setSubmitting] = useState(false);
  const [flowError, setFlowError] = useState(null);

  const load = useCallback(() => {
    const svc = new DiagnosisService();
    svc.trend(
      patientId,
      diagnosis.diagnosisId,
      (res) => setData(res),
      (err) => setError((err && err.message) || "Failed to load trend")
    );
  }, [patientId, diagnosis.diagnosisId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!patientId) return;
    // Parallel fetches: complaint + lab labels populate the audit table's
    // Description/Evidence bullets alongside the row's description text,
    // and medicine labels drive the audit's Medicines column.
    fetchMedicines(patientId).then((rows) => {
      const map = {};
      for (const r of rows) map[r.id] = r.label;
      setMedicineIndex(map);
    });
    fetchComplaints(patientId).then((rows) => {
      const map = {};
      for (const r of rows) map[r.id] = r.label;
      setComplaintIndex(map);
    });
    fetchLabs(patientId).then((rows) => {
      const map = {};
      for (const r of rows) map[r.id] = r.label;
      setLabIndex(map);
    });
  }, [patientId]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !submitting && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const currentBand = data ? treatmentBand(data.ongoing ? "ongoing" : "resolved") : null;
  const auditRows = useMemo(() => (data && data.audit) || [], [data]);
  // Table renders newest → oldest by `sinceWhen` so the reviewer sees
  // the most-recent change first regardless of the order the server
  // shipped rows in. `auditRows` stays in append order because the
  // correct / mark-incorrect API is indexed against that position;
  // `displayAudit` is a sorted view whose entries carry their original
  // index for backend calls. Missing / unparseable dates sort to the
  // bottom.
  const displayAudit = useMemo(
    () =>
      auditRows
        .map((row, originalIdx) => {
          const t = row && row.sinceWhen ? new Date(row.sinceWhen).getTime() : NaN;
          return { row, originalIdx, _t: isNaN(t) ? -Infinity : t };
        })
        .sort((a, b) => b._t - a._t),
    [auditRows]
  );
  // Chart uses the corrected picture only — server also excludes MarkedIncorrect
  // from its own derived stats. Local filter is the belt to the server's braces.
  const chartAudit = useMemo(
    () => auditRows.filter((e) => !e.markedIncorrect),
    [auditRows]
  );

  // Prefer the fresh version bumped by the last mutation over the initial
  // trend response — otherwise the second mutation in a row 409's.
  const expectedVersion =
    (data && data.version) ||
    (diagnosis && diagnosis.__version) ||
    0;

  const medicineLabels = useCallback(
    (refs) => {
      if (!refs || refs.length === 0) return [];
      return refs.map((raw) => {
        const id = String(raw);
        if (medicineIndex[id]) return medicineIndex[id];
        const short = id.length > 6 ? `#${id.slice(-6)}` : `#${id}`;
        return short;
      });
    },
    [medicineIndex]
  );
  // Sibling resolvers for Description/Evidence bullets. Unresolved ids
  // are dropped (null) so a slow /complaints or /labs fetch doesn't
  // surface raw hex placeholders — the bullet cell just skips them.
  const complaintLabels = useCallback(
    (refs) => (refs || []).map((raw) => complaintIndex[String(raw)] || null),
    [complaintIndex]
  );
  const labLabels = useCallback(
    (refs) => (refs || []).map((raw) => labIndex[String(raw)] || null),
    [labIndex]
  );

  const reporterLabel = (row) => {
    if (row && row.reportedByName) return row.reportedByName;
    const actor = row && row.reportedBy;
    if (!actor) return "—";
    const who = actor.who ? actor.who.charAt(0).toUpperCase() + actor.who.slice(1) : "";
    return actor.whoId ? `${who || "User"} #${actor.whoId}` : who || "—";
  };

  const openReview = (row, index) => {
    setFlowError(null);
    setFlagTarget({ row, index });
    if (row.markedIncorrect) {
      setDialogMode("incorrect");
    } else if (row.correction) {
      setDialogMode("history");
    } else {
      setDialogMode("review");
    }
  };

  const closeAll = () => {
    setDialogMode(null);
    setFlagTarget(null);
    setSuccessMode(null);
    setFlowError(null);
  };

  const afterMutation = (mode) => {
    setSubmitting(false);
    setDialogMode(null);
    setSuccessMode(mode);
    setFlowError(null);
    EventService.emit(DX_V2_REFRESH);
    load(); // refresh the audit inside the modal too
  };

  const failMutation = (err) => {
    setSubmitting(false);
    setFlowError((err && err.message) || "Save failed");
  };

  const submitMarkIncorrect = () => {
    if (!flagTarget || submitting) return;
    setSubmitting(true);
    setFlowError(null);
    const svc = new DiagnosisService();
    svc.markHistoryIncorrect(
      patientId,
      diagnosis.diagnosisId,
      flagTarget.index,
      expectedVersion,
      () => afterMutation("incorrect"),
      failMutation
    );
  };

  const submitCorrect = (payload) => {
    if (!flagTarget || submitting) return;
    setSubmitting(true);
    setFlowError(null);
    const body = { ...payload, expectedVersion };
    const svc = new DiagnosisService();
    svc.correctHistory(
      patientId,
      diagnosis.diagnosisId,
      flagTarget.index,
      body,
      () => afterMutation("corrected"),
      failMutation
    );
  };

  return (
    <ModalPortal>
      <div className="cc-modal-backdrop" onMouseDown={submitting ? null : onClose}>
        <div
          className="cc-modal cc-modal-trend"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <header className="cc-modal-head">
            <div>
              <span className="cc-modal-title">{diagnosis.diagnosis}</span>
              {currentBand && (
                <span
                  className="cc-modal-tag"
                  style={{ color: currentBand.fg, background: currentBand.bg }}
                >
                  {currentBand.label}
                </span>
              )}
            </div>
            <button type="button" className="cc-modal-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </header>
  
          {!data && !error && <div className="cc-modal-body cc-modal-loading">Loading…</div>}
          {error && <div className="cc-modal-body cc-modal-error">{error}</div>}
  
          {data && (
            <div className="cc-modal-body">
              <div className="cc-trend-stats">
                <div>
                  <span className="cc-trend-lbl">Episodes</span>
                  <span className="cc-trend-val">{data.episodes}</span>
                </div>
                <div>
                  <span className="cc-trend-lbl">First</span>
                  <span className="cc-trend-val">{formatShortDate(data.first)}</span>
                </div>
                <div>
                  <span className="cc-trend-lbl">Latest</span>
                  <span className="cc-trend-val">{formatShortDate(data.latest)}</span>
                </div>
              </div>
  
              <div className="cc-trend-chart-wrap">
                <div className="cc-trend-chart-head">
                  <span className="cc-trend-chart-title">Treatment status over time</span>
                  <span className="cc-trend-chart-range">
                    {formatShortDate(data.first)} – {formatShortDate(data.latest)}
                  </span>
                </div>
                <TreatmentStatusChart audit={chartAudit} />
                {(() => {
                  // Ongoing → recent episode's start date (first entry of
                  // current segment). Resolved → sinceWhen of the audit
                  // entry that closed the episode. Backend sends
                  // `currentSinceWhen` as the zero-time when resolved,
                  // which formats as "01 Jan 1" — derive locally instead.
                  const label = data.ongoing ? "Ongoing since:" : "Resolved on:";
                  const value = data.ongoing
                    ? recentEpisodeSinceWhen(data.audit, data.currentSinceWhen)
                    : recentResolveSinceWhen(data.audit, null);
                  if (!value) return null;
                  return (
                    <p className="cc-trend-chart-latest">
                      {label}{" "}
                      <strong style={{ color: currentBand && currentBand.fg }}>
                        {formatShortDate(value)}
                      </strong>
                    </p>
                  );
                })()}
              </div>
  
              <div className="cc-trend-audit">
                <div className="cc-trend-audit-head">
                  <span>Audit</span>
                  <span className="cc-trend-audit-count">
                    · {auditRows.length} entries
                  </span>
                </div>
                <div className="sec-table dx-trend-grid dx-trend-grid-flag" role="table">
                  <div className="sec-thead" role="row">
                    <span role="columnheader">Since when</span>
                    <span role="columnheader">Description / evidence</span>
                    <span role="columnheader">To when</span>
                    <span role="columnheader">Medicines</span>
                    <span role="columnheader">Reported by</span>
                    <span role="columnheader">Reported on</span>
                    <span role="columnheader">Treatment status</span>
                    <span role="columnheader" aria-label="Correction" />
                  </div>
                  {displayAudit.map(({ row, originalIdx }) => {
                    const isDelete = row.event === "delete";
                    const band = treatmentBand(row.ongoing ? "ongoing" : "resolved");
                    const meds = medicineLabels(row.medicineRefs);
                    // Live row = highest original index in append order;
                    // sits at the top of the descending view.
                    const isLive = originalIdx === auditRows.length - 1;
                    // Description/Evidence bullet content — same rule as
                    // the main table: description (when non-blank), then
                    // linked chief complaints, then linked lab values.
                    const evidenceBullets = [];
                    if (row.description && row.description.trim()) {
                      evidenceBullets.push(row.description.trim());
                    }
                    for (const c of complaintLabels(row.complaintIds)) if (c) evidenceBullets.push(c);
                    for (const l of labLabels(row.labRefs)) if (l) evidenceBullets.push(l);
                    const state = rowState(row);
                    return (
                      <div
                        className={`sec-row${state === "incorrect" ? " is-marked" : ""}`}
                        role="row"
                        key={originalIdx}
                      >
                        <span className="sec-cell" role="cell">
                          {formatShortDate(row.sinceWhen)}
                        </span>
                        <span className="sec-cell dg3-c-bullets" role="cell">
                          <BulletListCell items={evidenceBullets} />
                        </span>
                        <span className="sec-cell" role="cell">
                          {row.toWhen ? formatShortDate(row.toWhen) : "—"}
                        </span>
                        <span className="sec-cell dx-trend-meds dg3-c-bullets" role="cell">
                          <BulletListCell items={meds} />
                        </span>
                        <span className="sec-cell" role="cell">
                          {reporterLabel(row)}
                        </span>
                        <span className="sec-cell med-trend-ts" role="cell">
                          <span className="med-trend-ts-date">
                            {formatShortDate(row.recordedAt)}
                          </span>
                          <span className="med-trend-ts-time">{timeOnly(row.recordedAt)}</span>
                        </span>
                        <span className="sec-cell" role="cell">
                          {isDelete ? (
                            <span className="cc-audit-deleted">Deleted</span>
                          ) : (
                            <span
                              className="cc-trend-band"
                              style={{ background: band.bg, color: band.fg }}
                            >
                              {band.label}
                            </span>
                          )}
                        </span>
                        <span className="sec-cell cc-trend-flag-cell" role="cell">
                          {/* Skip the flag/icon on the live row — hasn't
                              been superseded, so there is nothing to
                              correct or mark incorrect on it. Backend
                              indexes on the original append-order
                              position, so `originalIdx` is passed
                              regardless of the descending display. */}
                          {!isDelete && !isLive && (
                            <button
                              type="button"
                              className={`cc-trend-flag${state !== "live" ? " is-flagged" : ""}`}
                              onClick={() => openReview(row, originalIdx)}
                              aria-label={
                                state === "incorrect"
                                  ? "View incorrect-mark audit"
                                  : state === "corrected"
                                  ? "View correction history"
                                  : "Review entry"
                              }
                              title={
                                state === "incorrect"
                                  ? "View incorrect-mark audit"
                                  : state === "corrected"
                                  ? "View correction history"
                                  : "Review entry"
                              }
                            >
                              <FlagIcon active={state !== "live"} />
                            </button>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {flowError && (
                  <div className="cc-modal-error dx-trend-flow-error">{flowError}</div>
                )}
              </div>
            </div>
          )}
        </div>
  
        {dialogMode === "review" && flagTarget && (
          <ReviewEntryDialog
            submitting={submitting}
            onMarkIncorrect={submitMarkIncorrect}
            onCorrect={() => setDialogMode("correct")}
            onClose={closeAll}
          />
        )}
        {dialogMode === "correct" && flagTarget && (
          <CorrectEntryDialog
            row={flagTarget.row}
            diagnosisName={diagnosis.diagnosis}
            medicineLabels={medicineLabels}
            submitting={submitting}
            onCancel={closeAll}
            onSave={submitCorrect}
          />
        )}
        {dialogMode === "history" && flagTarget && (
          <CorrectionHistoryDialog
            row={flagTarget.row}
            diagnosisName={diagnosis.diagnosis}
            medicineLabels={medicineLabels}
            onClose={closeAll}
          />
        )}
        {dialogMode === "incorrect" && flagTarget && (
          <MarkedIncorrectDialog row={flagTarget.row} onClose={closeAll} />
        )}
        {successMode && (
          <SuccessDialog mode={successMode} onDone={closeAll} />
        )}
      </div>
    </ModalPortal>
  );
};

TrendModal.propTypes = {
  patientId: PropTypes.number.isRequired,
  diagnosis: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TrendModal;
